import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstitutionalDashboard } from '../lib/queries';
import { institutionAPI } from '../lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Building2, FolderKanban, DollarSign, TrendingUp, AlertTriangle,
  BarChart3, ShieldAlert, UserPlus, Copy, X, Clock, CheckCircle2
} from 'lucide-react';
import { formatCurrency, formatPercent } from '../lib/utils';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const stagger = (i) => ({ initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, delay: i * 0.04 } });

function KPI({ label, value, subtitle, icon: Icon, color, delay = 0 }) {
  return (
    <motion.div {...stagger(delay)}>
      <Card className="rounded-xl border-border/50 shadow-sm" data-testid={`inst-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">{label}</p>
              <p className="text-2xl font-display font-semibold tabular-nums mt-1.5">{value}</p>
              {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
            </div>
            {Icon && (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}12` }}>
                <Icon className="h-5 w-5" style={{ color }} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function RiskBadge({ count }) {
  if (count === 0) return <Badge className="text-[10px]" style={{ background: 'var(--success-soft)', color: 'var(--success)', border: 'none' }}>Sin riesgo</Badge>;
  if (count <= 2) return <Badge className="text-[10px]" style={{ background: 'var(--warning-soft)', color: 'var(--warning-color)', border: 'none' }}>{count} alerta{count > 1 ? 's' : ''}</Badge>;
  return <Badge className="text-[10px]" style={{ background: 'var(--danger-soft)', color: 'var(--danger)', border: 'none' }}>{count} alertas</Badge>;
}

export default function InstitutionalDashboardPage() {
  const { data, isLoading } = useInstitutionalDashboard();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ company_name: '', owner_email: '', owner_name: '' });
  const [inviting, setInviting] = useState(false);
  const [lastCode, setLastCode] = useState(null);

  const { data: invites = [] } = useQuery({
    queryKey: ['institutionInvites'],
    queryFn: () => institutionAPI.listInvites().then(r => r.data),
    staleTime: 30 * 1000,
    enabled: !!data,
  });
  const pendingInvites = invites.filter(i => i.status === 'pending');

  const handleInvite = async () => {
    if (!inviteForm.company_name || !inviteForm.owner_email || !inviteForm.owner_name) {
      toast.error('Completa todos los campos'); return;
    }
    setInviting(true);
    try {
      const res = await institutionAPI.invite(inviteForm);
      setLastCode(res.data.code);
      toast.success('Invitación creada');
      setInviteForm({ company_name: '', owner_email: '', owner_name: '' });
      qc.invalidateQueries({ queryKey: ['institutionInvites'] });
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setInviting(false);
  };

  const handleCancelInvite = async (id) => {
    try {
      await institutionAPI.cancelInvite(id);
      toast.success('Invitación cancelada');
      qc.invalidateQueries({ queryKey: ['institutionInvites'] });
    } catch { toast.error('Error'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Código copiado');
  };

  // Pre-piloto · 2026-02: memoizamos los dos rankings (top TIR y empresas en riesgo).
  // Deben declararse ANTES de cualquier early return (regla de hooks React).
  const companies = data?.companies || [];
  const topByIrr = useMemo(() => (
    [...companies]
      .filter(c => c.avg_irr != null)
      .sort((a, b) => (b.avg_irr || 0) - (a.avg_irr || 0))
      .slice(0, 5)
  ), [companies]);
  const withIrrCount = useMemo(() => (
    companies.filter(c => c.avg_irr != null).length
  ), [companies]);
  const topByRisk = useMemo(() => (
    companies
      .filter(c => c.high_risk > 0)
      .sort((a, b) => b.high_risk - a.high_risk)
      .slice(0, 5)
  ), [companies]);
  const anyAtRisk = topByRisk.length > 0;

  if (isLoading) {
    return (
      <div className="px-6 lg:px-8 py-6" data-testid="inst-dashboard-loading">
        <Skeleton className="h-8 w-80 mb-1" />
        <Skeleton className="h-4 w-48 mb-8" />
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!data || data.companies_count === 0) {
    return (
      <div className="px-6 lg:px-8 py-6" data-testid="inst-dashboard-empty">
        <div className="text-center py-20">
          <Building2 className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
          <h2 className="text-xl font-display font-semibold mb-2">Portfolio Institucional</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            No hay empresas asociadas a esta institución. Agrega empresas desde Configuración para ver el consolidado del portfolio.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 lg:px-8 py-6 max-w-[1440px]" data-testid="inst-dashboard-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mb-7 flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="inst-title">
            {data.institution_name || 'Portfolio Institucional'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Consolidado de {data.companies_count} empresa{data.companies_count > 1 ? 's' : ''} · {data.projects_count} proyectos
          </p>
        </div>
        <Button size="sm" className="gap-1.5 text-xs text-white" style={{ background: 'var(--accent-primary)' }} onClick={() => setShowInvite(!showInvite)} data-testid="invite-company-btn">
          <UserPlus className="w-3.5 h-3.5" /> Invitar empresa
        </Button>
      </motion.div>

      {/* Invite panel */}
      {showInvite && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mb-6">
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-display font-semibold">Invitar empresa al portfolio</h3>
                <button onClick={() => { setShowInvite(false); setLastCode(null); }}><X className="w-4 h-4 text-muted-foreground" /></button>
              </div>

              {lastCode ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--success)' }} />
                  <p className="text-sm font-medium mb-2">Comparte este código de invitación:</p>
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <code className="text-lg font-mono font-bold px-4 py-2 rounded-lg" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)' }} data-testid="invite-code">{lastCode}</code>
                    <Button variant="outline" size="sm" onClick={() => copyCode(lastCode)} data-testid="copy-code-btn"><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground">El owner de la empresa debe ir a Configuración → "Unirse a institución" e ingresar este código.</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => setLastCode(null)}>Crear otra invitación</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nombre de la empresa</Label>
                    <Input placeholder="Desarrollos XYZ" value={inviteForm.company_name} onChange={e => setInviteForm(p => ({...p, company_name: e.target.value}))} data-testid="invite-company-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Nombre del contacto</Label>
                    <Input placeholder="Juan Pérez" value={inviteForm.owner_name} onChange={e => setInviteForm(p => ({...p, owner_name: e.target.value}))} data-testid="invite-owner-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email del contacto</Label>
                    <div className="flex gap-2">
                      <Input placeholder="juan@empresa.com" value={inviteForm.owner_email} onChange={e => setInviteForm(p => ({...p, owner_email: e.target.value}))} data-testid="invite-owner-email" />
                      <Button size="sm" className="shrink-0 text-white" style={{ background: 'var(--accent-primary)' }} onClick={handleInvite} disabled={inviting} data-testid="send-invite-btn">
                        {inviting ? 'Creando...' : 'Crear'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pending invites */}
              {pendingInvites.length > 0 && !lastCode && (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border-default)' }}>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Invitaciones pendientes ({pendingInvites.length})</p>
                  <div className="space-y-2">
                    {pendingInvites.map(inv => (
                      <div key={inv.id} className="flex items-center gap-3 text-xs">
                        <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="font-medium">{inv.company_name}</span>
                        <span className="text-muted-foreground">{inv.owner_email}</span>
                        <code className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-elevated)' }}>{inv.code}</code>
                        <Button variant="ghost" size="sm" className="ml-auto h-6 text-[10px]" onClick={() => copyCode(inv.code)}><Copy className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive" onClick={() => handleCancelInvite(inv.id)}><X className="w-3 h-3" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-8" data-testid="inst-kpis">
        <KPI label="Empresas" value={data.companies_count} subtitle="En el portfolio" icon={Building2} color="var(--accent-primary)" delay={0} />
        <KPI label="Proyectos" value={data.projects_count} subtitle="Activos" icon={FolderKanban} color="var(--accent-primary)" delay={1} />
        <KPI label="Capital Total" value={formatCurrency(data.total_capital)} subtitle="Peak equity consolidado" icon={DollarSign} color="var(--success)" delay={2} />
        <KPI label="TIR Promedio" value={data.avg_irr != null ? formatPercent(data.avg_irr) : 'N/A'} subtitle="Ponderada del portfolio" icon={TrendingUp} color="var(--warning-color)" delay={3} />
        <KPI label="Alto Riesgo" value={data.high_risk_projects} subtitle="Proyectos con VAN<0 o TIR<10%" icon={ShieldAlert} color="var(--danger)" delay={4} />
      </div>

      {/* Companies Table */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="rounded-xl border-border/50 shadow-sm" data-testid="inst-companies-table">
          <CardContent className="p-0">
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-default)' }}>
              <div>
                <h2 className="text-sm font-display font-semibold">Empresas del Portfolio</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">Métricas consolidadas por empresa</p>
              </div>
              <Badge variant="outline" className="text-[10px]">{companies.length} empresas</Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Empresa</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Proyectos</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Capital</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">TIR Prom.</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-center">Riesgo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((c, i) => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-muted/30" data-testid={`inst-company-${i}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: 'var(--accent-primary)' }}>
                          {c.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <span className="text-sm font-medium">{c.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{c.projects}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{formatCurrency(c.capital)}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm" style={{ color: c.avg_irr != null ? (c.avg_irr >= 15 ? 'var(--success)' : c.avg_irr >= 10 ? 'var(--warning-color)' : 'var(--danger)') : 'var(--text-muted)' }}>
                      {c.avg_irr != null ? formatPercent(c.avg_irr) : 'N/A'}
                    </TableCell>
                    <TableCell className="text-center"><RiskBadge count={c.high_risk} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </motion.div>

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
                <h3 className="text-xs font-display font-semibold uppercase tracking-wider text-muted-foreground">Distribución por Riesgo</h3>
              </div>
              <div className="space-y-2">
                {(() => {
                  const safe = companies.filter(c => c.high_risk === 0).length;
                  const warn = companies.filter(c => c.high_risk > 0 && c.high_risk <= 2).length;
                  const crit = companies.filter(c => c.high_risk > 2).length;
                  const total = companies.length || 1;
                  return [
                    { label: 'Sin riesgo', count: safe, color: 'var(--success)', pct: safe/total*100 },
                    { label: 'Riesgo moderado', count: warn, color: 'var(--warning-color)', pct: warn/total*100 },
                    { label: 'Riesgo alto', count: crit, color: 'var(--danger)', pct: crit/total*100 },
                  ].map(r => (
                    <div key={r.label} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: r.color }} />
                      <span className="text-xs flex-1">{r.label}</span>
                      <span className="text-xs font-semibold tabular-nums">{r.count}</span>
                      <div className="w-16 h-1.5 rounded-full bg-muted">
                        <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: r.color }} />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4" style={{ color: 'var(--success)' }} />
                <h3 className="text-xs font-display font-semibold uppercase tracking-wider text-muted-foreground">Top Empresas por TIR</h3>
              </div>
              <div className="space-y-2.5">
                {topByIrr.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <span className="text-[10px] font-bold w-4 text-muted-foreground">{i + 1}</span>
                    <span className="text-xs flex-1 truncate">{c.name}</span>
                    <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--success)' }}>{formatPercent(c.avg_irr)}</span>
                  </div>
                ))}
                {withIrrCount === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2">Sin análisis ejecutados</p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4" style={{ color: 'var(--danger)' }} />
                <h3 className="text-xs font-display font-semibold uppercase tracking-wider text-muted-foreground">Empresas en Riesgo</h3>
              </div>
              <div className="space-y-2.5">
                {topByRisk.map(c => (
                  <div key={c.id} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: c.high_risk > 2 ? 'var(--danger)' : 'var(--warning-color)' }} />
                    <span className="text-xs flex-1 truncate">{c.name}</span>
                    <span className="text-xs font-semibold tabular-nums" style={{ color: 'var(--danger)' }}>{c.high_risk} proyecto{c.high_risk > 1 ? 's' : ''}</span>
                  </div>
                ))}
                {!anyAtRisk && (
                  <p className="text-xs text-center py-2" style={{ color: 'var(--success)' }}>Ninguna empresa en riesgo</p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
